import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { 
  Megaphone, Plus, Calendar, Clock, Users, Eye, MousePointer,
  TrendingUp, Play, Pause, MoreVertical, Filter, Search,
  Mail, MessageSquare, Instagram, Facebook, Linkedin,
  Edit2, Trash2, X, Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const campaignTypes = [
  { id: 'email', label: 'Email Campaign', icon: Mail, color: 'text-blue-400' },
  { id: 'social', label: 'Social Media', icon: Instagram, color: 'text-pink-400' },
  { id: 'sms', label: 'SMS Campaign', icon: MessageSquare, color: 'text-emerald-400' },
];

export default function Campaigns() {
  const { token } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    type: 'email',
    startDate: '',
    endDate: '',
    budget: ''
  });

  useEffect(() => {
    fetchCampaigns();
  }, [token]);

  const fetchCampaigns = async () => {
    try {
      const response = await fetch(`${API}/api/marketing/campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCampaigns(data.campaigns || []);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || campaign.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-emerald-500/20 text-emerald-400';
      case 'paused': return 'bg-amber-500/20 text-amber-400';
      case 'scheduled': return 'bg-blue-500/20 text-blue-400';
      case 'completed': return 'bg-zinc-500/20 text-zinc-400';
      default: return 'bg-zinc-500/20 text-zinc-400';
    }
  };

  const getTypeIcon = (type) => {
    const typeConfig = campaignTypes.find(t => t.id === type);
    return typeConfig ? typeConfig : campaignTypes[0];
  };

  const toggleCampaignStatus = async (campaign) => {
    const newStatus = campaign.status === 'active' ? 'paused' : 'active';
    try {
      const response = await fetch(`${API}/api/marketing/campaigns/${campaign.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        setCampaigns(campaigns.map(c => c.id === campaign.id ? { ...c, status: newStatus } : c));
        toast.success(`Campaign ${newStatus === 'active' ? 'activated' : 'paused'}`);
      }
    } catch (error) {
      toast.error('Failed to update campaign');
    }
    setShowActionsMenu(null);
  };

  const handleCreateCampaign = async () => {
    if (!newCampaign.name || !newCampaign.startDate) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API}/api/marketing/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newCampaign)
      });
      
      if (response.ok) {
        const data = await response.json();
        setCampaigns([data.campaign, ...campaigns]);
        setNewCampaign({ name: '', type: 'email', startDate: '', endDate: '', budget: '' });
        setShowCreateModal(false);
        toast.success('Campaign created successfully');
      } else {
        toast.error('Failed to create campaign');
      }
    } catch (error) {
      toast.error('Error creating campaign');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      const response = await fetch(`${API}/api/marketing/campaigns/${campaignId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        setCampaigns(campaigns.filter(c => c.id !== campaignId));
        toast.success('Campaign deleted');
      }
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
    setShowActionsMenu(null);
  };

  const stats = {
    total: campaigns.length,
    active: campaigns.filter(c => c.status === 'active').length,
    totalBudget: campaigns.reduce((sum, c) => sum + (c.budget || 0), 0),
    totalSpent: campaigns.reduce((sum, c) => sum + (c.spent || 0), 0)
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-primary" />
            Campaigns
          </h1>
          <p className="text-muted-foreground">Create and manage your marketing campaigns</p>
        </div>
        <button 
          onClick={() => setShowCreateModal(true)}
          className="marketing-btn-primary flex items-center gap-2"
          data-testid="create-campaign-btn"
        >
          <Plus className="w-4 h-4" />
          <span>Create Campaign</span>
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="metric-card">
          <p className="text-xs text-muted-foreground">Total Campaigns</p>
          <p className="text-2xl font-bold">{stats.total}</p>
        </div>
        <div className="metric-card">
          <p className="text-xs text-muted-foreground">Active Campaigns</p>
          <p className="text-2xl font-bold text-emerald-400">{stats.active}</p>
        </div>
        <div className="metric-card">
          <p className="text-xs text-muted-foreground">Total Budget</p>
          <p className="text-2xl font-bold">${stats.totalBudget.toLocaleString()}</p>
        </div>
        <div className="metric-card">
          <p className="text-xs text-muted-foreground">Total Spent</p>
          <p className="text-2xl font-bold text-primary">${stats.totalSpent.toLocaleString()}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search campaigns..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="marketing-input pl-10 w-full"
            data-testid="campaign-search"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'active', 'paused', 'scheduled'].map(status => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-2 rounded-lg text-sm capitalize transition-colors ${
                filterStatus === status 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-secondary hover:bg-secondary/80'
              }`}
              data-testid={`filter-${status}`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Campaigns List */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredCampaigns.length > 0 ? (
          filteredCampaigns.map(campaign => {
          const TypeIcon = getTypeIcon(campaign.type);
          return (
            <div key={campaign.id} className="marketing-card hover:border-primary/30 transition-colors" data-testid={`campaign-${campaign.id}`}>
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                {/* Campaign Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-lg bg-secondary flex items-center justify-center ${TypeIcon.color}`}>
                      <TypeIcon.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold truncate">{campaign.name}</h3>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Calendar className="w-3 h-3" />
                        <span>{campaign.startDate} - {campaign.endDate || 'Ongoing'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-4 gap-4 text-center lg:w-96">
                  <div>
                    <p className="text-lg font-bold">{(campaign.impressions / 1000).toFixed(1)}K</p>
                    <p className="text-xs text-muted-foreground">Impressions</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold">{(campaign.clicks / 1000).toFixed(1)}K</p>
                    <p className="text-xs text-muted-foreground">Clicks</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold">{campaign.conversions}</p>
                    <p className="text-xs text-muted-foreground">Conversions</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-primary">${campaign.spent}</p>
                    <p className="text-xs text-muted-foreground">Spent</p>
                  </div>
                </div>

                {/* Status & Actions */}
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(campaign.status)}`}>
                    {campaign.status}
                  </span>
                  <button 
                    onClick={() => toggleCampaignStatus(campaign)}
                    className="p-2 hover:bg-secondary rounded-lg transition-colors"
                    data-testid={`toggle-${campaign.id}`}
                  >
                    {campaign.status === 'active' ? (
                      <Pause className="w-4 h-4 text-amber-400" />
                    ) : (
                      <Play className="w-4 h-4 text-emerald-400" />
                    )}
                  </button>
                  <div className="relative">
                    <button 
                      onClick={() => setShowActionsMenu(showActionsMenu === campaign.id ? null : campaign.id)}
                      className="p-2 hover:bg-secondary rounded-lg transition-colors"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    {showActionsMenu === campaign.id && (
                      <div className="absolute right-0 top-full mt-1 w-40 bg-card border border-border rounded-lg shadow-lg z-50 py-1">
                        <button
                          onClick={() => { /* Edit logic */ setShowActionsMenu(null); }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-secondary transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteCampaign(campaign.id)}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Budget Progress */}
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Budget: ${campaign.budget}</span>
                  <span className="text-muted-foreground">{Math.round((campaign.spent / campaign.budget) * 100)}% used</span>
                </div>
                <div className="h-2 bg-secondary rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary rounded-full transition-all duration-500"
                    style={{ width: `${Math.min((campaign.spent / campaign.budget) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          );
        })
        ) : (
          <div className="marketing-card text-center py-12">
            <Megaphone className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No campaigns found</p>
            <button 
              onClick={() => setShowCreateModal(true)}
              className="marketing-btn-primary mt-4"
            >
              Create Your First Campaign
            </button>
          </div>
        )}
      </div>

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-md p-6 animate-fade-in" data-testid="create-campaign-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Create New Campaign</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-secondary rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Campaign Name</label>
                <input
                  type="text"
                  value={newCampaign.name}
                  onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})}
                  placeholder="Enter campaign name"
                  className="marketing-input w-full"
                  data-testid="campaign-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Campaign Type</label>
                <div className="grid grid-cols-3 gap-2">
                  {campaignTypes.map(type => (
                    <button
                      key={type.id}
                      onClick={() => setNewCampaign({...newCampaign, type: type.id})}
                      className={`p-3 rounded-lg border transition-colors flex flex-col items-center gap-2 ${
                        newCampaign.type === type.id
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <type.icon className={`w-5 h-5 ${type.color}`} />
                      <span className="text-xs">{type.label.split(' ')[0]}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Start Date</label>
                  <input
                    type="date"
                    value={newCampaign.startDate}
                    onChange={(e) => setNewCampaign({...newCampaign, startDate: e.target.value})}
                    className="marketing-input w-full"
                    data-testid="campaign-start-date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">End Date</label>
                  <input
                    type="date"
                    value={newCampaign.endDate}
                    onChange={(e) => setNewCampaign({...newCampaign, endDate: e.target.value})}
                    className="marketing-input w-full"
                    data-testid="campaign-end-date"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Budget ($)</label>
                <input
                  type="number"
                  value={newCampaign.budget}
                  onChange={(e) => setNewCampaign({...newCampaign, budget: e.target.value})}
                  placeholder="Enter budget"
                  className="marketing-input w-full"
                  data-testid="campaign-budget"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-4 py-2 border border-border rounded-lg hover:bg-secondary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCampaign}
                disabled={saving}
                className="flex-1 marketing-btn-primary flex items-center justify-center gap-2"
                data-testid="save-campaign-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Create Campaign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
